// src/modules/estimates/EstimateView.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import Select from '../../components/shared/Select';
import Map from '../../components/shared/Map';
import { getEstimateById, getServices, getSuppliersByLocation, updateEstimateStatus, updateEstimateStatusOnly } from './EstimateService';
import SendEstimateModal from './SendEstimateModal';
import ChangeOrderListSection from '../change-orders/ChangeOrderListSection';
import { FaUser, FaListAlt, FaMap, FaCalendarPlus, FaFilePdf, FaEdit, FaFileAlt, FaArrowLeft, FaTools, FaEnvelope, FaLink, FaCopy, FaExternalLinkAlt, FaFileSignature, FaEye, FaPhone, FaImages, FaHardHat} from 'react-icons/fa';
import { fetchJobs } from '../jobs/jobsService';
import { useCommunication } from '../communications/context/CommunicationContext';
import { calculateDistance } from '../../utils/distanceUtils';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { Navigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/utils';
import { discountInfo } from '../../utils/discount';
import { getUnitInfo } from './ServiceModal';
import { ActionBar, DetailField, DetailGrid, DetailHeader, DetailTitle } from '../../components/styled/DetailComponents';
import { ActionButton, StatusBadge } from '../../components/styled/CommonStyles';
import InfoCard from '../../components/shared/InfoCard';
import SupplierListComponent from '../../components/Suppliers/SupplierListComponent';
import FeedbackModal from '../../components/shared/ModalNotification';
import Modal from '../../components/shared/Modal';
import { generateEstimatePDF } from '../../services/estimateService';
import { createInvoice, fetchInvoiceByEstimate } from '../financial/financialService';
import BillToModal from '../financial/BillToModal';
import WeatherForecast from '../../components/weather/WeatherForecast';
import { getEstimateAddress, formatAddress } from '../../utils/estimateAddressUtils';
import OriginBadge from '../../components/shared/OriginBadge';

const AddrTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${props => props.theme.typography.fontSize.small};
  background: ${props => props.theme.colors.primary}22;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  vertical-align: middle;
`;

const PriceValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xlarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.success};
  text-align: right;
  margin-top: ${props => props.theme.spacing(2)};
`;

const ServiceCard = styled.div`
  padding: ${props => props.theme.spacing(3)};
  border: 1px solid ${props => props.theme.colors.inputGray}22;
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: ${props => props.theme.spacing(2)};

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing(2)};
  }

  .service-name {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.primary};
  }

  .details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: ${props => props.theme.spacing(2)};
  }

  .detail-item {
    label {
      display: block;
      color: ${props => props.theme.colors.textSecondary};
      font-size: ${props => props.theme.typography.fontSize.small};
      margin-bottom: ${props => props.theme.spacing(0.5)};
    }
    
    span {
      font-weight: ${props => props.theme.typography.fontWeight.medium};
    }
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 400px;
  margin-top: ${(props) => props.theme.spacing(4)};
`;

const HeaderFlex = styled.div`
  display: flex !important;
  align-items: center;
  gap: ${props => props.theme.spacing(3)};
  justify-content: space-between !important;

`;

const StatusSelect = styled(Select)`
  min-width: 100px;
  padding: 10px !important;
  margin: 0px;
  font-size: 0.9em !important;
  border-color: ${props => {
    switch (props.value) {
      case 'Draft': return props.theme.colors.warning;
      case 'Sent': return props.theme.colors.info;
      case 'Viewed': return props.theme.colors.primary;
      case 'Accepted': return props.theme.colors.success;
      case 'Declined': return props.theme.colors.error;
      default: return props.theme.colors.inputGray;
    }
  }};

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const EstimateImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing(2)};
`;

const EstimateImageThumb = styled.div`
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  background: ${props => props.theme.colors.surface};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: 140px;
    object-fit: cover;
    display: block;
  }
`;

const EstimateImageLabel = styled.div`
  padding: 6px 10px;
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  text-align: center;
`;

const EstimateView = () => {
  const { estimateId } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [activeTab, setActiveTab] = useState('map');
  const navigate = useNavigate();
  const { openCallModal } = useCommunication();
const [showFeedback, setShowFeedback] = useState(false);
const [feedbackMessage, setFeedbackMessage] = useState('');
const [feedbackSuccess, setFeedbackSuccess] = useState(true);
const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
const [existingInvoice, setExistingInvoice] = useState(null);
const [billToModalOpen, setBillToModalOpen] = useState(false);
const [services, setServices] = useState([]);
const [linkedJob, setLinkedJob] = useState(null);

  useEffect(() => {
    if (estimateId) {
      fetchEstimateDetails(estimateId);
      fetchInvoiceByEstimate(estimateId).then(inv => setExistingInvoice(inv)).catch(() => {});
      getServices().then(res => setServices(res.data || [])).catch(() => {});
      fetchJobs()
        .then((jobs) => {
          const list = Array.isArray(jobs) ? jobs : [];
          const match = list.find((j) => String(j.estimate_id) === String(estimateId));
          setLinkedJob(match || null);
        })
        .catch(() => {});
    }
  }, [estimateId]);

  const fetchEstimateDetails = async (id) => {
    setLoading(true);
    try {
      const response = await getEstimateById(id);
      if (response.data) {
        setEstimate(response.data);
        setSelectedStatus(response.data.status);
        const addr = getEstimateAddress(response.data);
        if (addr.latitude && addr.longitude) {
          fetchSuppliers(addr.latitude, addr.longitude);
        }
      }
    } catch (error) {
      console.error('Error fetching estimate details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async (lat, lng) => {
    try {
      const response = await getSuppliersByLocation(lat, lng, 20);
      const fetchedSuppliers = response.data;

      const suppliersWithCategories = fetchedSuppliers.map((supplier) => ({
        ...supplier,
        distance: calculateDistance(lat, lng, supplier.latitude, supplier.longitude).toFixed(2),
        categories: supplier.categories || [],
      }));

      const sortedSuppliers = suppliersWithCategories.sort((a, b) => a.distance - b.distance);
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!estimate) {
    return <div>No estimate found.</div>;
  }

  const formattedSuppliers = suppliers.map((supplier) => ({
    lat: supplier.latitude,
    lng: supplier.longitude,
    title: supplier.name,
    distance: supplier.distance,
    products: supplier.categories.map((category) => category.name),
    isSupplier: true,
  }));

  const canEdit = !['Accepted', 'Sent', 'Declined'].includes(estimate?.status);
  
  const handleEdit = () => {
    navigate(`/estimates/edit/${estimateId}`);
  };

  const validTransitions = {
    'Draft': ['Sent', 'Accepted'],
    'Sent': ['Viewed', 'Accepted', 'Declined', 'Expired', 'Draft'],
    'Viewed': ['Accepted', 'Declined', 'Expired', 'Draft'],
    'Accepted': ['Completed'], // Terminal state
    'Declined': ['Draft'], // Terminal state
    'Expired': ['Draft'] ,  // Terminal state
    'Completed': ['Draft']
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    
    if (!validTransitions[estimate.status]?.includes(newStatus)) {
      setFeedbackMessage('Invalid status transition');
      setFeedbackSuccess(false);
      setShowFeedback(true);
      return;
    }
  
    try {
      setLoading(true);
      const response = await updateEstimateStatusOnly(estimate.id, { newStatus });
      
      if (response.status === 200) {
        setEstimate(prev => ({ ...prev, status: newStatus }));
        setFeedbackMessage(`Status updated to ${newStatus}`);
        setFeedbackSuccess(true);
        
        // If status is Accepted, show different message
        if (newStatus === 'Accepted') {
          setFeedbackMessage('Estimate accepted! You can now schedule the job.');
        }
      }
    } catch (error) {
      setFeedbackMessage(error.message || 'Error updating status');
      setFeedbackSuccess(false);
    } finally {
      setLoading(false);
      setShowFeedback(true);
    }
  };
  
  // Add this feedback handler
  const handleFeedbackComplete = () => {
    setShowFeedback(false);
    // Redirect to job creation if status was changed to Accepted
    if (feedbackSuccess && estimate.status === 'Accepted') {
      navigate(`/jobs/new/${estimate.id}`);
    }
  };

  // `recipient` is the address chosen in the picker, or null to let the server
  // use the lead's primary address (the single-address case).
  const handleSendEmail = async (recipient) => {
    setSendEmailModalOpen(false);
    try {
      setLoading(true);
      const payload = recipient ? { newStatus: 'Sent', to_email: recipient } : { newStatus: 'Sent' };
      const response = await updateEstimateStatus(estimate.id, payload);
      if (response?.status === 200) {
        setEstimate(prev => ({ ...prev, status: 'Sent' }));
        setFeedbackMessage(`Estimate sent to ${recipient || estimate.Lead?.email}! Quote link delivered via email and SMS.`);
        // Refresh to get publicToken
        fetchEstimateDetails(estimateId);
        setFeedbackSuccess(true);
      } else {
        // updateEstimateStatus swallows its error and returns undefined, so
        // without this the modal would show the previously-set message.
        setFeedbackMessage('Error sending estimate. Please try again.');
        setFeedbackSuccess(false);
      }
    } catch (error) {
      setFeedbackMessage(error.message || 'Error sending estimate');
      setFeedbackSuccess(false);
    } finally {
      setLoading(false);
      setShowFeedback(true);
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return '#3498db';
      case 'In Progress': return '#f69620';
      case 'Completed': return '#27ae60';
      case 'Cancelled': return '#c9595c';
      default: return '#666666';
    }
  };

  const handleGeneratePDF = async () => {
    try {
      setLoading(true); // Assuming you have a loading state
      await generateEstimatePDF(estimateId);
      setLoading(false);
    } catch (error) {
      setFeedbackMessage('Failed to generate PDF. Please try again.'); // Assuming you have an error state
      setFeedbackSuccess(false)
      setLoading(false);
    }
  };
  

  return (
    <DashboardLayout>
      <DetailHeader>
    <HeaderFlex>
      <DetailTitle>
        <FaFileAlt size={24} />
        <h1>Estimate #{estimate.id}</h1>
        {linkedJob && (
          <StatusBadge
            as="a"
            href={`/jobs/${linkedJob.job_id}`}
            onClick={(e) => { e.preventDefault(); navigate(`/jobs/${linkedJob.job_id}`); }}
            color={getJobStatusColor(linkedJob.status)}
            title={`Job ${linkedJob.status}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', cursor: 'pointer', marginLeft: '8px' }}
          >
            <FaHardHat /> Job {linkedJob.status}
          </StatusBadge>
        )}
      </DetailTitle>

      <StatusSelect
        value={estimate.status}
        onChange={handleStatusChange}
        disabled={!validTransitions[estimate.status]?.length}
        label={"Status"}
      >
        <option value={estimate.status}>{estimate.status}</option>
        {validTransitions[estimate.status]?.map(status => (
          <option key={status} value={status}>{status}</option>
        ))}
      </StatusSelect>
    </HeaderFlex>

    <ActionBar>
      <ActionButton onClick={() => navigate('/estimates')}>
        <FaArrowLeft /> Back
      </ActionButton>
      {!['Accepted', 'Sent', 'Declined'].includes(estimate.status) && (
        <ActionButton variant="primary" onClick={() => navigate(`/estimates/edit/${estimate.id}`)}>
          <FaEdit /> Edit
        </ActionButton>
      )}

<ActionButton
  onClick={handleGeneratePDF}
  disabled={loading}
  variant="primary"
>
  <FaFilePdf />
  {loading ? 'Generating...' : 'Generate PDF'}
</ActionButton>
      {['Draft', 'Sent', 'Viewed'].includes(estimate.status) && (
        <ActionButton variant="primary" onClick={() => setSendEmailModalOpen(true)} disabled={loading}>
          <FaEnvelope /> Send Email
        </ActionButton>
      )}
      {estimate.status === 'Accepted' && !linkedJob && (
        <ActionButton variant="success" onClick={() => navigate(`/jobs/new/${estimate.id}`)}>
          <FaCalendarPlus /> Schedule Job
        </ActionButton>
      )}
      {linkedJob && (
        <ActionButton variant="primary" onClick={() => navigate(`/jobs/edit/${linkedJob.job_id}`)}>
          <FaEdit /> Edit Job
        </ActionButton>
      )}
      {estimate.status === 'Accepted' && !existingInvoice && (
        <ActionButton variant="primary" onClick={() => setBillToModalOpen(true)}>
          <FaFileAlt /> Generate Invoice
        </ActionButton>
      )}
      {existingInvoice && (
        <ActionButton variant="primary" onClick={() => navigate(`/financial/invoices/${existingInvoice.id}`)}>
          <FaFileAlt /> View Invoice
        </ActionButton>
      )}
    </ActionBar>
  </DetailHeader>

      <InfoCard title="Lead Information" icon={<FaUser />}>
        <DetailGrid>
          <DetailField>
            <label>Name</label>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <a
                href={`/leads/view/${estimate.Lead.id}`}
                onClick={(e) => { e.preventDefault(); navigate(`/leads/view/${estimate.Lead.id}`); }}
                style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              >
                {estimate.Lead.name} <FaExternalLinkAlt size={10} />
              </a>
              {estimate.Lead.origin?.origin_name && (
                <OriginBadge origin={estimate.Lead.origin} size="sm" />
              )}
            </span>
          </DetailField>
          <DetailField>
            <label>Email</label>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {estimate.Lead.email}
              {estimate.Lead.email && (
                <FaCopy
                  size={14}
                  style={{ cursor: 'pointer', opacity: 0.6, flexShrink: 0 }}
                  title="Copy email"
                  onClick={() => {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(estimate.Lead.email).then(() => {
                        setFeedbackMessage('Email copied to clipboard!');
                        setFeedbackSuccess(true);
                        setShowFeedback(true);
                      }).catch(() => {
                        // fallback
                        const ta = document.createElement('textarea');
                        ta.value = estimate.Lead.email;
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                        setFeedbackMessage('Email copied to clipboard!');
                        setFeedbackSuccess(true);
                        setShowFeedback(true);
                      });
                    } else {
                      const ta = document.createElement('textarea');
                      ta.value = estimate.Lead.email;
                      ta.style.position = 'fixed';
                      ta.style.opacity = '0';
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                      setFeedbackMessage('Email copied to clipboard!');
                      setFeedbackSuccess(true);
                      setShowFeedback(true);
                    }
                  }}
                />
              )}
            </span>
          </DetailField>
          <DetailField>
            <label>Phone</label>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {estimate.Lead.phone}
              {estimate.Lead.phone && (
                <FaPhone
                  size={14}
                  style={{ cursor: 'pointer', opacity: 0.6, flexShrink: 0 }}
                  title="Call this number"
                  onClick={() => openCallModal(estimate.Lead.phone, estimate.Lead.id, estimate.Lead.name)}
                />
              )}
            </span>
          </DetailField>
          <DetailField>
            <label>Quote Address</label>
            <span>
              {formatAddress(getEstimateAddress(estimate))}
              {estimate.leadAddress && (
                <AddrTag>{estimate.leadAddress.label || 'Alternate address'}</AddrTag>
              )}
            </span>
          </DetailField>
          <DetailField>
            <label>Readiness</label>
            <span>{estimate.Lead.when_ready}</span>
          </DetailField>
          <DetailField>
            <label>Origin</label>
            <span>{estimate.Lead.origin?.origin_name}</span>
          </DetailField>
          {estimate.Lead.job_observation && (
            <DetailField>
              <label>Notes</label>
              <span>{estimate.Lead.job_observation}</span>
            </DetailField>
          )}
        </DetailGrid>
      </InfoCard>

      {(() => {
        const addr = getEstimateAddress(estimate);
        return addr.latitude && addr.longitude ? (
          <WeatherForecast
            latitude={parseFloat(addr.latitude)}
            longitude={parseFloat(addr.longitude)}
            title={`Weather at ${addr.street || 'Job Site'}`}
          />
        ) : null;
      })()}

      {estimate.publicToken && (
        <InfoCard title="Public Quote Link" icon={<FaLink />}>
          <DetailGrid>
            <DetailField>
              <label>Quote URL</label>
              <span style={{ wordBreak: 'break-all', fontSize: '0.85em' }}>
                {`https://crm.mrpatchasphalt.com/quote/${estimate.publicToken}`}
              </span>
            </DetailField>
            <DetailField>
              <label>Actions</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <ActionButton onClick={() => {
                  const url = `https://crm.mrpatchasphalt.com/quote/${estimate.publicToken}`;
                  navigator.clipboard.writeText(url);
                  setFeedbackMessage('URL copied to clipboard!');
                  setFeedbackSuccess(true);
                  setShowFeedback(true);
                }}>
                  <FaCopy /> Copy URL
                </ActionButton>
                <ActionButton onClick={() => {
                  const token = localStorage.getItem('token');
                  const url = `https://crm.mrpatchasphalt.com/quote/${estimate.publicToken}?preview=${token}`;
                  window.open(url, '_blank');
                }}>
                  <FaExternalLinkAlt /> Preview
                </ActionButton>
              </div>
            </DetailField>
          </DetailGrid>
        </InfoCard>
      )}

      {estimate.signature && (
        <InfoCard title="Agreement Signature" icon={<FaFileSignature />}>
          <DetailGrid>
            <DetailField>
              <label>Signed By</label>
              <span>{estimate.signature.fullName}</span>
            </DetailField>
            <DetailField>
              <label>Email</label>
              <span>{estimate.signature.email}</span>
            </DetailField>
            <DetailField>
              <label>Signed At</label>
              <span>{new Date(estimate.signature.signedAt).toLocaleString()}</span>
            </DetailField>
            <DetailField>
              <label>IP Address</label>
              <span>{estimate.signature.ipAddress}</span>
            </DetailField>
          </DetailGrid>
        </InfoCard>
      )}

      {estimate.views && estimate.views.length > 0 && (
        <InfoCard title={`Quote Views (${estimate.views.length})`} icon={<FaEye />}>
          {estimate.views.map((view, index) => (
            <DetailGrid key={index} style={{ marginBottom: index < estimate.views.length - 1 ? '12px' : 0, paddingBottom: index < estimate.views.length - 1 ? '12px' : 0, borderBottom: index < estimate.views.length - 1 ? '1px solid #eee' : 'none' }}>
              <DetailField>
                <label>Email</label>
                <span>{view.email}</span>
              </DetailField>
              <DetailField>
                <label>Viewed At</label>
                <span>{new Date(view.viewedAt).toLocaleString()}</span>
              </DetailField>
            </DetailGrid>
          ))}
        </InfoCard>
      )}

      <InfoCard title="Services" icon={<FaTools />}>
        {estimate.items.map((service, index) => (
          <ServiceCard key={index}>
            <div className="header">
              <span className="service-name">{service.Service.service_name}</span>
            </div>
            <div className="details">
              <div className="detail-item">
                <label>Area</label>
                <span>{service.totalArea} {getUnitInfo(service.measurementUnit).short}</span>
              </div>
              <div className="detail-item">
                <label>Rate</label>
                <span>{formatCurrency(service.pricePerSqft)}/{getUnitInfo(service.measurementUnit).short}</span>
              </div>
              <div className="detail-item">
                <label>Subtotal</label>
                <span>{formatCurrency(service.totalPrice)}</span>
              </div>
            </div>
          </ServiceCard>
        ))}
        
        {(() => {
          const discount = discountInfo(estimate, estimate.originalSubtotal ?? estimate.subtotal);
          if (!discount.hasDiscount) return null;
          return (
            <DetailField>
              <label>Discount Applied</label>
              <span>
                -{formatCurrency(discount.amount)}
                {discount.percentage && discount.type === 'percentage' ? ` (${discount.percentage}%)` : ''}
                {discount.reason ? ` - ${discount.reason}` : ''}
              </span>
            </DetailField>
          );
        })()}

        {(() => {
          const original = estimate.originalTotalPrice != null ? parseFloat(estimate.originalTotalPrice) : null;
          const current = parseFloat(estimate.totalPrice);
          const hasBreakdown = original != null && Math.abs(original - current) > 0.001;
          if (!hasBreakdown) {
            return <PriceValue>Total {formatCurrency(estimate.totalPrice)}</PriceValue>;
          }
          return (
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <div style={{ fontSize: '0.9em', color: '#888' }}>
                Original quote {formatCurrency(original)}
              </div>
              <PriceValue>Total {formatCurrency(current)}</PriceValue>
            </div>
          );
        })()}
      </InfoCard>

      <ChangeOrderListSection
        estimate={estimate}
        estimateId={estimate.id}
        estimateStatus={estimate.status}
        changeOrders={estimate.changeOrders || []}
        services={services}
        onChange={() => fetchEstimateDetails(estimateId)}
      />

      {estimate.details?.length > 0 && (
        <InfoCard title="Additional Details" icon={<FaListAlt />}>
          <DetailGrid>
            {estimate.details.map((detail, index) => (
              <DetailField key={index}>
                <span>{detail.name}</span>
              </DetailField>
            ))}
          </DetailGrid>
        </InfoCard>
      )}

      {estimate.images?.length > 0 && (
        <InfoCard title={`Attachments (${estimate.images.length})`} icon={<FaImages />}>
          <EstimateImageGrid>
            {estimate.images.map((image) => (
              <EstimateImageThumb key={image.id}>
                <img
                  src={`${(process.env.REACT_APP_API_URL || '').replace(/\/api$/, '')}/estimate-images/${image.filename}`}
                  alt={image.originalName}
                />
                <EstimateImageLabel>{image.displaySize === 'full' ? 'Full Width' : image.displaySize === 'half' ? '2 Columns' : '3 Columns'}</EstimateImageLabel>
              </EstimateImageThumb>
            ))}
          </EstimateImageGrid>
        </InfoCard>
      )}

      <InfoCard title="Suppliers Nearby" icon={<FaMap />}>
        <MapWrapper>
          {(() => {
            const addr = getEstimateAddress(estimate);
            return addr.latitude && addr.longitude ? (
              <Map
                addresses={[
                  {
                    lat: addr.latitude,
                    lng: addr.longitude,
                    title: 'Job Site',
                    isSupplier: false,
                  },
                  ...formattedSuppliers,
                ]}
              />
            ) : null;
          })()}
        </MapWrapper>
           {suppliers.length > 0 ? (
              <SupplierListComponent suppliers={suppliers} />
            ) : (
              <></>
            )}
      </InfoCard>

      <SendEstimateModal
        isOpen={sendEmailModalOpen}
        estimateId={estimate.id}
        lead={estimate.Lead}
        title="Send Estimate Email"
        onConfirm={handleSendEmail}
        onCancel={() => setSendEmailModalOpen(false)}
      />

      <BillToModal
        isOpen={billToModalOpen}
        leadName={estimate.Lead?.name}
        title="Generate Invoice"
        submitLabel="Generate Invoice"
        onCancel={() => setBillToModalOpen(false)}
        onSubmit={async ({ billToName, billToAddress }) => {
          // Billing the lead sends { estimateId } only, same as before.
          const options = billToName ? { billToName, billToAddress } : {};
          const inv = await createInvoice(estimate.id, options);
          setBillToModalOpen(false);
          setExistingInvoice(inv);
          setFeedbackSuccess(true);
          setFeedbackMessage(`Invoice ${inv.invoiceNumber} created!`);
          setShowFeedback(true);
        }}
      />
      <FeedbackModal
      isOpen={showFeedback}
      message={feedbackMessage}
      success={feedbackSuccess}
      onComplete={handleFeedbackComplete}
    />
    
    {loading && <LoadingSpinner />}
      
    </DashboardLayout>
  );
};

export default EstimateView;